/* eslint-env es6 */
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Button,
  Switch,
  Alert,
  Tooltip,
} from 'antd';
import {useQuery} from '@apollo/client';
import {GET_ALL_COUNTRY_CODES} from 'graphql/queries/trendingCountries';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {usePlatformCurrencies} from './usePlatformCurrencies';
import {
  referralPartnerTypes,
  retiredReferralPartnerTypes,
  academyCommissionTypes,
  academyCommissionBases,
} from './utils';
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import styles from './referralPartnersForm.module.scss';

// The rows below are label-less: the column name lives here in the header, so
// this is where a per-column tooltip has to go. antd's Form.Item `tooltip`
// renders inside the label element and is dropped entirely when there is no
// label, which is why it has to be spelled out rather than passed as a prop.
const ColumnHead = ({children, tooltip}) => (
  <span className={styles.columnHead}>
    {children}
    {tooltip && (
      <Tooltip title={tooltip}>
        <QuestionCircleOutlined className={styles.columnHeadIcon} />
      </Tooltip>
    )}
  </span>
);

const {Option} = Select;

const Section = ({title, description, children}) => (
  <section className={styles.section}>
    <div className={styles.sectionHead}>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

export const ReferralPartnersForm = ({
  form,
  onSubmit,
  onCancel,
  loading,
  isEdit,
}) => {
  const {data: countryCodesData, loading: loadingCountryCodes} = useQuery(
    GET_ALL_COUNTRY_CODES,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const countryCodes = countryCodesData?.getAllCountryCodes?.countries || [];
  const email = Form.useWatch('email', form);
  const referralPartnerType = Form.useWatch('referralPartnerType', form);
  const academyEnabled = Form.useWatch('academyCommissionEnabled', form);
  const academyRates = Form.useWatch('academyCommissionRates', form);

  // "None" is no longer a type anyone can pick. A partner saved under it before
  // it was retired keeps it until an admin chooses a real one, so its option is
  // put back for that partner alone — without it, antd falls back to showing the
  // raw "NONE" in the field.
  const referralTypeOptions = [
    ...referralPartnerTypes,
    ...retiredReferralPartnerTypes.filter(
      type => type.value === referralPartnerType,
    ),
  ];

  const configuredCurrencies = (academyRates || [])
    .map(rate => rate?.currency)
    .filter(Boolean);
  // Fetched rather than hardcoded: a currency switched off centrally must stop
  // being offered here without a release, and a rate in a currency no tracker
  // uses earns nothing.
  const {currencies, loading: loadingCurrencies} = usePlatformCurrencies();

  const missingCurrencies = currencies
    .map(currency => currency.value)
    .filter(currency => !configuredCurrencies.includes(currency));

  /** Every currency field on this form offers the same live, enabled-only list. */
  const currencyOptions = currencies.map(currency => (
    <Option key={currency.value} value={currency.value}>
      {currency.value}
    </Option>
  ));

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1>
            {isEdit ? 'Edit Referral Partner' : 'Create Referral Partner'}
          </h1>
          <p>
            {isEdit
              ? 'Update this partner’s details, investment tiers and Academy commission.'
              : 'Set up a new partner, their investment tiers and their Academy commission.'}
          </p>
        </div>
        {isEdit && email && (
          <Tooltip title={email}>
            <span className={styles.identity}>{email}</span>
          </Tooltip>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        className={styles.form}
        onFinish={onSubmit}
        onFinishFailed={() => {
          toast.error('Please fill in all required fields');
        }}>
        <div className={styles.body}>
          <Section
            title="Partner Details"
            description="Who this partner is and how they sign in to the partner portal.">
            <Row gutter={20}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {required: true, message: 'Email is required'},
                    {type: 'email', message: 'Please enter a valid email'},
                  ]}>
                  <Input placeholder="partner@example.com" />
                </Form.Item>
              </Col>
              {!isEdit && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Portal Password"
                    name="password"
                    extra="At least 8 characters. Share it with the partner over a secure channel."
                    rules={[
                      {required: true, message: 'Password is required'},
                      {
                        min: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    ]}>
                    <Input.Password placeholder="At least 8 characters" />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12}>
                <Form.Item label="First Name" name="firstName">
                  <Input placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Last Name" name="lastName">
                  <Input placeholder="Enter last name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Phone Number" name="phoneNumber">
                  <Input.Group compact>
                    <Form.Item name="countryCode" noStyle>
                      <Select
                        placeholder="Code"
                        style={{width: '32%'}}
                        loading={loadingCountryCodes}
                        showSearch
                        optionFilterProp="filter"
                        filterOption={true}>
                        {countryCodes.map(country => (
                          <Option
                            filter={country.phone?.toString()}
                            key={
                              country.id || `${country.phone}-${country.code}`
                            }
                            value={country.phone}>
                            +{country.phone}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="phoneNumber" noStyle>
                      <Input
                        style={{width: '68%'}}
                        placeholder="Enter phone number"
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Referral Partner Type"
                  name="referralPartnerType"
                  rules={[
                    {
                      required: true,
                      message: 'Referral partner type is required',
                    },
                  ]}>
                  <Select placeholder="Select referral partner type">
                    {referralTypeOptions.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Referral Code"
                  name="referralCode"
                  extra="Leave empty to let the system generate one.">
                  <Input placeholder="e.g. PARTNER20" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Spreadsheet ID"
                  name="spreadSheetId"
                  extra="Optional — the Google Sheet this partner’s reporting is synced to.">
                  <Input placeholder="Enter spreadsheet ID" />
                </Form.Item>
              </Col>
              {isEdit && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Portal Account Locked"
                    name="isAccountLocked"
                    valuePropName="checked"
                    extra="Only saved if you change it. Switch off to restore a revoked partner.">
                    <Switch />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Section>

          <Section
            title="Investment Commission"
            description="Share of trading fees this partner earns, stepped by volume. One row per currency.">
            {/* One rate per currency, not a single Float. A flat figure cannot
                mean the same thing against an XOF tracker and an NGN one, so
                the gateway takes a list and rejects the old scalar shape. */}
            <Form.Item
              label="Commission on New Invested Users"
              tooltip={
                <>
                  <p>
                    A one-off bonus the partner earns each time someone they
                    referred makes their first investment. Paid once per person.
                  </p>
                  <p style={{marginBottom: 0}}>
                    Set an amount for each currency — the bonus paid is the one
                    matching the currency your referral invested in. No amount
                    set for a currency means no bonus is paid on it.
                  </p>
                </>
              }
              extra="A flat amount per referee who makes their first investment, per currency.">
              <Form.List name="referralPartnerCommissionOnNewInvestedUsers">
                {(fields, {add, remove}) => (
                  <>
                    {fields.length > 0 && (
                      <div className={`${styles.rowsHead} ${styles.rateGrid}`}>
                        <span>Currency</span>
                        <span>Amount</span>
                        <span />
                        <span />
                      </div>
                    )}

                    {fields.length === 0 ? (
                      <div className={styles.empty}>
                        No rates set — this partner earns nothing on new
                        invested users.
                      </div>
                    ) : (
                      fields.map(({key, name, ...restField}) => (
                        <div
                          key={key}
                          className={`${styles.row} ${styles.rateGrid}`}>
                          <Form.Item
                            {...restField}
                            name={[name, 'currency']}
                            rules={[
                              {required: true, message: 'Currency is required'},
                            ]}>
                            <Select
                              showSearch
                              placeholder="Currency"
                              loading={loadingCurrencies}>
                              {currencyOptions}
                            </Select>
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'amount']}
                            rules={[
                              {required: true, message: 'Amount is required'},
                            ]}>
                            <InputNumber
                              style={{width: '100%'}}
                              placeholder="Amount"
                              className="input-number"
                              min={0}
                            />
                          </Form.Item>
                          <span />
                          <div className={styles.rowDelete}>
                            <Tooltip title="Remove rate">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ))
                    )}

                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      className={styles.addButton}>
                      Add Currency Rate
                    </Button>

                    {/* There is no cross-currency fallback, so an unset rate
                        has to be visible at the point of setting it. */}
                    {fields.length > 0 && missingCurrencies.length > 0 && (
                      <Alert
                        style={{marginBottom: 18}}
                        type="info"
                        showIcon
                        message={`No rate for ${missingCurrencies.join(', ')}`}
                        description="New invested users in those currencies earn this partner nothing."
                      />
                    )}
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.List name="referralPartnerPercentage">
              {(fields, {add, remove}) => (
                <>
                  {fields.length > 0 && (
                    <div className={`${styles.rowsHead} ${styles.tierGrid}`}>
                      <ColumnHead tooltip="The minimum total volume of trades this partner’s referees must make to start earning">
                        Volume Threshold
                      </ColumnHead>
                      <ColumnHead tooltip="The percentage of trading fees this partner earns once their referees’ total volume exceeds the threshold">
                        Cut on Fees (%)
                      </ColumnHead>
                      <ColumnHead tooltip="The currency this tier applies to. A partner can have multiple tiers in different currencies.">
                        Currency
                      </ColumnHead>
                      <span />
                    </div>
                  )}

                  {fields.length === 0 ? (
                    <div className={styles.empty}>
                      No volume tiers yet — this partner earns no share of
                      trading fees.
                    </div>
                  ) : (
                    fields.map(({key, name, ...restField}) => (
                      <div
                        key={key}
                        className={`${styles.row} ${styles.tierGrid}`}>
                        <Form.Item
                          {...restField}
                          name={[name, 'volumeThreshold']}
                          rules={[
                            {required: true, message: 'Threshold is required'},
                          ]}>
                          <InputNumber
                            style={{width: '100%'}}
                            placeholder="Volume threshold"
                            min={0}
                            className="input-number"
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'percentageCutOnFees']}
                          rules={[
                            {required: true, message: 'Percentage is required'},
                          ]}>
                          <InputNumber
                            style={{width: '100%'}}
                            placeholder="Cut on fees"
                            min={0}
                            className="input-number"
                            max={100}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'currency']}
                          rules={[
                            {required: true, message: 'Currency is required'},
                          ]}>
                          <Select
                            showSearch
                            placeholder="Currency"
                            loading={loadingCurrencies}>
                            {currencyOptions}
                          </Select>
                        </Form.Item>
                        <div className={styles.rowDelete}>
                          <Tooltip title="Remove tier">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ))
                  )}

                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}>
                    Add Volume Tier
                  </Button>
                </>
              )}
            </Form.List>
          </Section>

          <Section
            title="Daba Academy Commission"
            description="Paid on Academy course sales made with a promo code this partner owns. Rates are per currency — a sale in a currency with no rate is still attributed to the partner, it just earns nothing.">
            <div className={styles.toggleStrip}>
              <div className={styles.toggleRow}>
                <Form.Item
                  label="Pay Academy Commission"
                  name="academyCommissionEnabled"
                  valuePropName="checked"
                  initialValue={false}
                  style={{marginBottom: 0}}>
                  <Switch />
                </Form.Item>
              </div>
              <Form.Item
                label="Commission Basis"
                name="academyCommissionBasis"
                initialValue="GROSS"
                style={{marginBottom: 0}}>
                <Select placeholder="Select basis">
                  {academyCommissionBases.map(basis => (
                    <Option key={basis.value} value={basis.value}>
                      {basis.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {!academyEnabled && (
              <p className={styles.disabledNote}>
                Commission is switched off. Academy sales made with this
                partner’s codes are still attributed to them for reporting —
                they simply pay nothing until you switch this on.
              </p>
            )}

            <Form.List name="academyCommissionRates">
              {(fields, {add, remove}) => (
                <>
                  {fields.length > 0 && (
                    <div className={`${styles.rowsHead} ${styles.rateGrid}`}>
                      <span>Currency</span>
                      <span>Type</span>
                      <span>Value</span>
                      <span />
                    </div>
                  )}

                  {fields.length === 0 ? (
                    <div className={styles.empty}>
                      No Academy rates yet — add one per currency you sell
                      courses in.
                    </div>
                  ) : (
                    fields.map(({key, name, ...restField}) => (
                      <AcademyRateRow
                        key={key}
                        form={form}
                        name={name}
                        restField={restField}
                        onRemove={() => remove(name)}
                      />
                    ))
                  )}

                  <Button
                    type="dashed"
                    onClick={() => add({type: 'PERCENTAGE'})}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}>
                    Add Academy Rate
                  </Button>
                </>
              )}
            </Form.List>

            {academyEnabled && missingCurrencies.length > 0 && (
              <Alert
                style={{marginBottom: 18}}
                type="warning"
                showIcon
                message={`No rate for ${missingCurrencies.join(', ')}`}
                description="Sales in those currencies are attributed to this partner but pay no commission."
              />
            )}
          </Section>
        </div>

        <div className={styles.footer}>
          {onCancel && (
            <button type="button" className={styles.cancel} onClick={onCancel}>
              Cancel
            </button>
          )}
          <SubmitButton
            label={isEdit ? 'Save Changes' : 'Create Partner'}
            disabled={loading}
          />
        </div>
      </Form>
    </div>
  );
};

const AcademyRateRow = ({form, name, restField, onRemove}) => {
  // Same live list as the rest of the form. The query is cache-first, so this
  // costs nothing beyond the first row.
  const {currencies, loading: loadingCurrencies} = usePlatformCurrencies();
  const rateType = Form.useWatch(
    ['academyCommissionRates', name, 'type'],
    form,
  );
  const rateCurrency = Form.useWatch(
    ['academyCommissionRates', name, 'currency'],
    form,
  );
  const isPercentage = rateType !== 'FIXED';
  const unit = isPercentage ? '%' : rateCurrency || '—';

  return (
    <div className={`${styles.row} ${styles.rateGrid}`}>
      <Form.Item
        {...restField}
        name={[name, 'currency']}
        rules={[
          {
            required: true,
            message: 'Each academy commission rate requires a currency',
          },
        ]}>
        <Select showSearch placeholder="Currency" loading={loadingCurrencies}>
          {currencies.map(currency => (
            <Option key={currency.value} value={currency.value}>
              {currency.value}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        {...restField}
        name={[name, 'type']}
        initialValue="PERCENTAGE"
        rules={[{required: true, message: 'Type is required'}]}>
        <Select placeholder="Type">
          {academyCommissionTypes.map(type => (
            <Option key={type.value} value={type.value}>
              {type.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <div className={styles.valueField}>
        <Form.Item
          {...restField}
          name={[name, 'value']}
          style={{flex: 1, marginBottom: 0}}
          rules={[
            {required: true, message: 'Value must be greater than 0'},
            {
              validator: (_, value) => {
                if (value === undefined || value === null) {
                  return Promise.resolve();
                }
                if (!(Number(value) > 0)) {
                  return Promise.reject(
                    new Error('Value must be greater than 0'),
                  );
                }
                if (isPercentage && Number(value) > 100) {
                  return Promise.reject(
                    new Error('Percentage cannot exceed 100'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}>
          <InputNumber
            style={{width: '100%'}}
            className="input-number"
            min={0}
            max={isPercentage ? 100 : undefined}
            placeholder={isPercentage ? '15' : '5000'}
          />
        </Form.Item>
        <span className={styles.unit}>{unit}</span>
      </div>

      <div className={styles.rowDelete}>
        <Tooltip title="Remove rate">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </Tooltip>
      </div>
    </div>
  );
};
